/**
 * Persistent state for the phone app, in localStorage.
 *
 * Two things are stored: the device credential earned at pairing, and the
 * signaling endpoint. Nothing else, and never anything the PC did not issue.
 */

import type { DeviceCredential } from '../../shared/signaling/protocol.ts';

const CRED_KEY = 'jarvis.credential.v1';
const ENDPOINT_KEY = 'jarvis.endpoint.v1';
const PUBID_KEY = 'jarvis.devicePublicId.v1';

/**
 * Build-time default, overridable at runtime for a self-hosted Worker.
 *
 * `jarvis.10didimail.workers.dev` is the deployed signaling Worker; the Pages
 * env var `VITE_SIGNALING_URL` overrides it at build time.
 */
const DEFAULT_ENDPOINT =
  (import.meta.env.VITE_SIGNALING_URL as string | undefined) ??
  'https://jarvis.10didimail.workers.dev';

export function loadCredential(): DeviceCredential | null {
  const raw = localStorage.getItem(CRED_KEY);
  if (!raw) return null;
  try {
    const c = JSON.parse(raw) as DeviceCredential;
    /* A half-written credential is worse than none: it would make the app retry
       a connection that can never succeed. */
    if (!c.installId || !c.deviceId || !c.deviceSecret) return null;
    return c;
  } catch {
    return null;
  }
}

export function saveCredential(c: DeviceCredential): void {
  localStorage.setItem(CRED_KEY, JSON.stringify(c));
}

export function clearCredential(): void {
  localStorage.removeItem(CRED_KEY);
}

export function loadEndpoint(): string {
  return localStorage.getItem(ENDPOINT_KEY) ?? DEFAULT_ENDPOINT;
}

export function saveEndpoint(url: string): void {
  localStorage.setItem(ENDPOINT_KEY, url.replace(/\/+$/, ''));
}

/**
 * A stable public id for this browser profile. It is not a secret and carries
 * no authority; it only lets the PC recognise a repeat pairing attempt from the
 * same phone.
 */
export function devicePublicId(): string {
  let id = localStorage.getItem(PUBID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(PUBID_KEY, id);
  }
  return id;
}

/** A readable name for the PC's paired-devices list. */
export function guessDeviceName(): string {
  const ua = navigator.userAgent;
  const m = /\((?:Linux; )?(?:Android [\d.]+; )?([^;)]+)/.exec(ua);
  const model = m?.[1]?.trim();
  if (model && model.length < 40 && !/^Windows|^Macintosh|^X11/.test(model)) return model;
  return /iPhone|iPad/.test(ua) ? 'iPhone' : 'Téléphone';
}

/**
 * Pairing details arrive in the URL fragment, which browsers do not send to
 * servers and do not put in a Referer header. Read once, then erase.
 */
export function takePairingFromUrl(): { installId: string; token: string } | null {
  const hash = location.hash.replace(/^#/, '');
  if (!hash) return null;
  const p = new URLSearchParams(hash);
  const installId = p.get('i');
  const token = p.get('t');
  if (!installId || !token) return null;
  history.replaceState(null, '', location.pathname + location.search);
  return { installId, token };
}
